import React, { useCallback } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CreateRolesFormData, CreateRolesSchema } from '../schema/CreateRolesSchema';
import { useCreateRole } from '../hooks/useCreateRole';

interface CreateRoleDialogProps {
    show: boolean;
    onClose: () => void;
}

const CreateRoleDialog: React.FC<CreateRoleDialogProps> = ({
    show, onClose
}) => {
    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<CreateRolesFormData>({
        resolver: zodResolver(CreateRolesSchema),
    });

    const handleClose = () => {
        reset();
        onClose();
    }

    const { mutate, isPending } = useCreateRole();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: CreateRolesFormData) => {
        toast.loading('Creating role...', {
            id: 'create-role',
        });
        mutate(values, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['roles'].includes(key as string))
                });

                toast.success('Role created successfully', {
                    id: 'create-role',
                });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof CreateRolesFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to create role', { id: 'create-role' });
                    }
                } else {
                    toast.error((mutationError as any)?.response?.data?.message || 'Failed to create role', { id: 'create-role' });
                }
            }
        });
    }, [mutate]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Create Role</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
                    <div className="mb-3">
                        <label htmlFor="name" className="form-label">Name</label>
                        <input
                            type="text"
                            className={`form-control ${errors.name && 'is-invalid'}`}
                            id="name"
                            placeholder='Enter permission name'
                            {...register('name')}
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" type='submit' onClick={handleSubmit(onSubmit)} disabled={isPending}>
                    {isPending ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default CreateRoleDialog